import { MutableRefObject, useCallback, useRef, useState } from 'react';

interface Options {
  /** Minimum sidebar width in rem. */
  min: number;
  /** Absolute maximum sidebar width in rem (design cap). */
  max: number;
  /**
   * Optional ref whose `.current` is read on every mousemove as the effective
   * upper bound, allowing the caller to tighten the max dynamically (e.g. when
   * the right panel is at its minimum and should not be pushed further).
   * Falls back to `max` when not provided.
   */
  maxRef?: MutableRefObject<number>;
  /** Initial sidebar width in rem. */
  initial: number;
}

/** Returns the root font size in px (used to convert rem ↔ px). */
const getRootFontSizePx = (): number =>
  parseFloat(getComputedStyle(document.documentElement).fontSize);

/**
 * Resize hook for the left sidebar.
 * Width is stored and returned in rem so it scales with the user's
 * browser font-size preference. Mouse event coordinates (always px)
 * are converted at runtime via the document root font size.
 */
export const useResizableSidebar = ({ min, max, maxRef, initial }: Options) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const widthRef = useRef(initial);
  const rafRef = useRef<number | null>(null);

  const [width, setWidth] = useState(initial);

  const onMouseDown = useCallback(() => {
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (e: MouseEvent) => {
      if (!containerRef.current || !sidebarRef.current) return;

      const rootFsPx = getRootFontSizePx();
      const left = containerRef.current.getBoundingClientRect().left;
      const nextRem = (e.clientX - left) / rootFsPx;
      const effectiveMax = maxRef ? maxRef.current : max;
      const clamped = Math.min(effectiveMax, Math.max(min, nextRem));

      widthRef.current = clamped;

      if (rafRef.current) return;

      rafRef.current = requestAnimationFrame(() => {
        if (!sidebarRef.current || !containerRef.current) {
          rafRef.current = null;
          return;
        }

        const fs = getRootFontSizePx();
        sidebarRef.current.style.width = `${widthRef.current}rem`;

        // Flex compression guard: when the workspace (flex-1) hits its
        // min-content width it stops shrinking, and flex instead compresses the
        // sidebar (min-w-0) below its style.width. Detect this by reading the
        // sidebar's actual rendered width after a forced reflow and updating
        // widthRef to match. This keeps sidebarWidthRef accurate so
        // clampPanelToViewport in RightPanelShell can shrink or unpin the right
        // panel when there is genuinely no space left for the sidebar to grow.
        const renderedRem =
          sidebarRef.current.getBoundingClientRect().width / fs;
        if (widthRef.current - renderedRem > 0.5) {
          widthRef.current = renderedRem;
          // No need to re-set style.width — flex already displays the sidebar
          // at renderedRem.
        }

        rafRef.current = null;
      });
    };

    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // Cancel any pending RAF so it cannot overwrite widthRef after
      // setWidth has already committed the final value to React state.
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      // Sync flex compression check: commit the actual rendered width so the
      // value stored in React state (and persisted to localStorage) matches
      // what the browser displayed, not the pre-compression drag target.
      if (sidebarRef.current && containerRef.current) {
        const fs = getRootFontSizePx();
        sidebarRef.current.style.width = `${widthRef.current}rem`;
        const renderedRem =
          sidebarRef.current.getBoundingClientRect().width / fs;
        if (widthRef.current - renderedRem > 0.5) {
          widthRef.current = renderedRem;
          sidebarRef.current.style.width = `${renderedRem}rem`;
        }
      }

      const effectiveMax = maxRef ? maxRef.current : max;
      const finalWidth = Math.min(effectiveMax, Math.max(min, widthRef.current));
      setWidth(finalWidth);

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [min, max]);

  return {
    containerRef,
    sidebarRef,
    width,
    onMouseDown,
    widthRef,
    setWidth,
  };
};
